import { DurationFormat, FilterOption, Placeholder } from 'utils/constants';
import { formatDuration, formatPercent, getLatestConfirmedId } from 'utils/helper';

const DEFAULT_SUMMARY_STATS = { high: '0', low: '0', median: '0', mean: '0', aiAccuracy: '0' };

/**
 * Helper to calculate new filter state for multiselect with "All" option.
 * @param {string|Array<string>} currentSelection - Current selected ID(s).
 * @param {string} clickedOption - The ID of the option that was clicked.
 * @returns {string|Array<string>} New selection state.
 */
export const getMultiselectFilterValue = (currentSelection, clickedOption) => {
    const selectionArray = Array.isArray(currentSelection) ? currentSelection : [currentSelection];
    const isAllSelected = selectionArray.includes(FilterOption.ALL);

    if (clickedOption === FilterOption.ALL) {
        return isAllSelected ? currentSelection : FilterOption.ALL;
    }

    if (isAllSelected) {
        return [clickedOption];
    }

    if (selectionArray.includes(clickedOption)) {
        if (selectionArray.length > 1) {
            return selectionArray.filter((id) => id !== clickedOption);
        }
        return FilterOption.ALL;
    }

    return [...selectionArray, clickedOption];
};

/**
 * Helper to safely extract total confirmed grading duration from sessions.
 * @param {Object<string, Array<{ reason: string, duration: number }>>} sessions - Map of user IDs to session arrays.
 * @returns {number} Total duration in seconds.
 */
const getGradingDuration = (sessions) => {
    if (!sessions) return 0;
    return Object.values(sessions)
        .flat()
        .filter((s) => s.reason === 'confirm')
        .reduce((total, session) => total + (session.duration || 0), 0);
};

/**
 * Calculates the score for a specific criterion.
 * Priority: 1. Manual Override -> 2. Selected Option -> 3. Zero
 * @param {Object} folder - The submission object.
 * @param {Object} [folder.grade_overrides] - Map of criterion IDs to overridden scores.
 * @param {Object} [folder.grading] - Grading data.
 * @param {Object} [submission.grading.criteria] - Map of criterion IDs to selected option IDs.
 * @param {Object} c - The criterion object.
 * @param {string|number} c.id - The criterion ID.
 * @param {Array<{ id: string, rate: number|string }>} c.options - List of options.
 * @returns {number} The calculated score.
 */
export const getCriterionScore = (submission, criterion) => {
    const { id, options } = criterion;
    if (submission.grade_overrides?.[id] !== undefined) {
        return Number(submission.grade_overrides[id]);
    }

    const selectedOptionId = submission.grading?.criteria?.[id];
    if (selectedOptionId) {
        const option = options.find((opt) => opt.id === selectedOptionId);
        return option ? Number(option.rate) : 0;
    }

    return 0;
};

/**
 * Calculates the total score for a submission based on rubric and overrides.
 * @param {Object} folder - The submission object.
 * @param {Object} [folder.grade_overrides] - Contains `total` or specific criterion overrides.
 * @param {Object} rubric - The assignment rubric.
 * @param {Array<{ id: string, options: Array }>} rubric.criteria - List of criteria.
 * @returns {number} Total score.
 */
export const getSubmissionScore = (submission, rubric) => {
    if (submission.grade_overrides?.total !== undefined) {
        return Number(submission.grade_overrides.total);
    }

    if (!rubric?.criteria) return 0;

    return rubric.criteria.reduce((total, criterion) => {
        return total + getCriterionScore(submission, criterion);
    }, 0);
};

/**
 * Calculates accuracy between human and AI score.
 * @param {number} humanScore - The score given by the human.
 * @param {number} aiScore - The score suggested by AI.
 * @param {number} maxScore - The maximum possible score for this item.
 * @returns {number} Accuracy represented as a decimal (0.0 to 1.0).
 */
const calculateAccuracyRatio = (humanScore, aiScore, maxScore) => {
    if (maxScore <= 0) return humanScore === aiScore ? 1 : 0;
    return Math.max(0, 1 - Math.abs(humanScore - aiScore) / maxScore);
};

/**
 * Calculates time saved statistics based on predicted vs actual grading time.
 * @param {Array<{ grading: boolean, sessions: Object }>} submissions - List of submissions.
 * @param {number|string} predictedMinutesPerSubmission - Predicted time per submission.
 * @returns {{ savedPerSub: number|string, totalSaved: number|string }} Calculated values or '--' if invalid.
 */
export const calculateTimeSavedStats = (submissions, predictedMinutesPerSubmission) => {
    const gradedSubmissions = submissions.filter((s) => s.grading);
    const count = gradedSubmissions.length;
    const predictedTimeInMinutes = parseFloat(predictedMinutesPerSubmission);

    if (isNaN(predictedTimeInMinutes) || count === 0) {
        return { savedPerSub: Placeholder.NO_DATA, totalSaved: Placeholder.NO_DATA };
    }

    const totalSeconds = gradedSubmissions.reduce((acc, sub) => acc + getGradingDuration(sub.sessions), 0);
    const avgMinutes = totalSeconds / count / 60;
    const savedPerSub = predictedTimeInMinutes - avgMinutes;

    return {
        savedPerSub: parseFloat(savedPerSub.toFixed(2)),
        totalSaved: parseFloat((savedPerSub * count).toFixed(2)),
    };
};

/**
 * Calculates high, low, median, mean scores, and average AI accuracy for a set of numeric values.
 * @param {Array<number>} scores - Array of raw numeric scores (percentages).
 * @param {Array<number>} [accuracies] - Array of accuracy ratios (0.0 to 1.0).
 * @returns {{ high: string, low: string, median: string, mean: string, aiAccuracy: string }} Formatted strings fixed to 1 decimal.
 */
const calculateSummaryStats = (scores, accuracies = []) => {
    if (!scores.length) return DEFAULT_SUMMARY_STATS;

    const sorted = [...scores].sort((a, b) => a - b);
    const count = sorted.length;

    const high = sorted[count - 1];
    const low = sorted[0];
    const mean = sorted.reduce((a, b) => a + b, 0) / count;

    const mid = Math.floor(count / 2);
    const median = count % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];

    const format = (v) => String(parseFloat(v.toFixed(1)));

    let avgAccuracy = 0;

    if (accuracies?.length > 0) {
        const sum = accuracies.reduce((a, b) => a + b, 0);
        avgAccuracy = (sum / accuracies.length) * 100;
    }

    return {
        high: format(high),
        low: format(low),
        median: format(median),
        mean: format(mean),
        aiAccuracy: format(avgAccuracy),
    };
};

/**
 * Calculates high, low, median, and mean scores for the class.
 * @param {Array} submissions - List of submission objects.
 * @param {Object} assignment - Assignment object containing `rubric`.
 * @param {Object|string} filters - Filter configuration { criterion: string, grader: string }.
 * @returns {{ high: string, low: string, median: string, mean: string, aiAccuracy: string }} Class performance stats.
 */
export const calculateClassPerformanceStats = (submissions, assignment, filters) => {
    if (!submissions.length || !assignment) {
        return DEFAULT_SUMMARY_STATS;
    }

    const activeCriterion = filters?.criterion || FilterOption.ALL;
    const activeGrader = filters?.grader || FilterOption.ALL;

    let targetSubmissions = submissions;

    if (activeGrader !== FilterOption.ALL) {
        targetSubmissions = submissions.filter((s) => {
            const graderId = getLatestConfirmedId(s.sessions);
            if (Array.isArray(activeGrader)) {
                return activeGrader.includes(graderId);
            }
            return graderId === activeGrader;
        });
    }

    if (!targetSubmissions.length) {
        return DEFAULT_SUMMARY_STATS;
    }

    const rubric = assignment.rubric;
    const isAll = activeCriterion === FilterOption.ALL;
    let maxSectionScore = 1;

    // 2. FILTER LEVEL: Criterion
    if (isAll) {
        maxSectionScore = rubric.criteria.reduce(
            (acc, crit) => acc + Math.max(...crit.options.map((o) => Number(o.rate))),
            0
        );
    } else {
        const crit = rubric.criteria.find((c) => c.id === activeCriterion);
        maxSectionScore = crit ? Math.max(...crit.options.map((o) => Number(o.rate))) : 1;
    }

    const accuracies = [];

    // 3. Consolidated pass: Calculate student scores and collective AI accuracy
    const activeCriteria = isAll
        ? (rubric?.criteria || [])
        : (rubric?.criteria || []).filter((c) => c.id === activeCriterion);

    const scores = targetSubmissions.map((s) => {
        let rawScore = 0;
        const hasTotalOverride = isAll && s.grade_overrides?.total !== undefined;
        if (hasTotalOverride) rawScore = Number(s.grade_overrides.total);

        activeCriteria.forEach((crit) => {
            const humanRate = getCriterionScore(s, crit);
            if (!hasTotalOverride) {
                if (isAll) rawScore += humanRate;
                else if (activeCriteria[0] && crit.id === activeCriteria[0].id) rawScore = humanRate;
            }

            const aiGrading = s.ai?.grading?.[crit.id];
            const aiOption = crit.options?.find((o) => o.id === aiGrading?.optionId);
            if (aiOption) {
                const critMax = Math.max(...crit.options.map((o) => Number(o.rate)));
                accuracies.push(calculateAccuracyRatio(humanRate, Number(aiOption.rate), critMax));
            }
        });

        return (rawScore / (maxSectionScore || 1)) * 100;
    });

    return calculateSummaryStats(scores, accuracies);
};

/**
 * Prepares data for the Class Performance chart (line or grouped bar).
 * @param {Array} allSubmissions - All submissions.
 * @param {Array} filteredClassSubmissions - Submissions already filtered by the UI.
 * @param {Object} assignment - Assignment object.
 * @param {Object} filters - { criterion: string, grader: string }
 * @param {Array<{id: string, name: string}>} gradersList - List of graders.
 * @returns {{ type: string, labels: string[], datasets: Array<{ label: string, data: number[] }> } | null} Chart data.
 */
export const calculateClassPerformanceChartData = (
    allSubmissions,
    filteredClassSubmissions,
    assignment,
    filters,
    gradersList
) => {
    if (!assignment || !filteredClassSubmissions) return null;

    const { rubric } = assignment;

    if (filters.criterion === FilterOption.ALL) {
        const buckets = Array(11).fill(0);
        const labels = ['0', '10', '20', '30', '40', '50', '60', '70', '80', '90', '100'];

        const maxTotal = rubric.criteria.reduce((total, crit) => {
            const maxOptionScore = Math.max(...crit.options.map((option) => Number(option.rate)));
            return total + maxOptionScore;
        }, 0);

        filteredClassSubmissions.forEach((submission) => {
            const totalScore = getSubmissionScore(submission, rubric);
            const percentage = maxTotal > 0 ? (totalScore / maxTotal) * 100 : 0;

            const bucketIdx = Math.min(Math.floor(percentage / 10), 10);
            buckets[bucketIdx]++;
        });

        return {
            type: 'line',
            labels,
            datasets: [{ label: 'Number of Students', data: buckets }],
        };
    }

    const criterion = rubric.criteria.find((citerion) => citerion.id === filters.criterion);
    if (!criterion) return null;

    const labels = criterion.options.map((option) => option.title);

    const graderSubmissionsMap = new Map();

    allSubmissions.forEach((submission) => {
        if (!submission.grading) return;

        const graderId = getLatestConfirmedId(submission.sessions);
        const selectedOptionId = submission.grading.criteria?.[criterion.id];

        if (!graderSubmissionsMap.has(graderId)) {
            graderSubmissionsMap.set(graderId, new Map());
        }

        const optionsMap = graderSubmissionsMap.get(graderId);
        const currentCount = optionsMap.get(selectedOptionId) || 0;

        optionsMap.set(selectedOptionId, currentCount + 1);
    });

    const datasets = gradersList.map((grader) => {
        const isMatch =
            filters.grader === FilterOption.ALL ||
            (Array.isArray(filters.grader) ? filters.grader.includes(grader.id) : filters.grader === grader.id);
        const optionsMap = graderSubmissionsMap.get(grader.id);

        const data = isMatch
            ? criterion.options.map((opt) => optionsMap?.get(opt.id) || 0)
            : criterion.options.map(() => 0);

        return {
            label: grader.name,
            data,
            borderWidth: 1,
            borderRadius: 4,
            _isFilteredOut: !isMatch,
        };
    });

    return { type: 'bar', labels, datasets };
};

/**
 * Processes grader-level performance statistics.
 * @param {Array<{ owner_account_id: string, name: string }>} accountUsers - List of users.
 * @param {Array} allSubmissions - All submissions.
 * @param {Object} assignment - Assignment object.
 * @returns {Array<{ id: string, name: string, submissionsGraded: number, avgGrade: string, totalTime: string, aiAccuracy: string }>} Stats array.
 */
export const calculateGraderStats = (accountUsers, allSubmissions, assignment) => {
    if (!accountUsers || !allSubmissions || !assignment) return [];

    const statsMap = new Map(
        accountUsers.map((user) => [
            user.owner_account_id,
            {
                id: user.owner_account_id,
                name: user.name || user.id,
                gradedCount: 0,
                totalGradePoints: 0,
                totalMaxPoints: 0,
                totalTimeSeconds: 0,
                accuracySum: 0,
                accuracyCount: 0,
            },
        ])
    );

    const criteria = assignment.rubric.criteria;
    const criteriaMaxPoints = criteria.map((c) => Math.max(...c.options.map((o) => Number(o.rate))));
    const totalMaxPossible = criteriaMaxPoints.reduce((a, b) => a + b, 0);

    allSubmissions.forEach((submission) => {
        if (submission.sessions) {
            Object.entries(submission.sessions).forEach(([userId, sessionArray]) => {
                const stat = statsMap.get(userId);
                if (stat) {
                    stat.totalTimeSeconds += getGradingDuration({ [userId]: sessionArray });
                }
            });
        }

        if (submission.grading) {
            const finalGraderId = getLatestConfirmedId(submission.sessions);
            const stat = statsMap.get(finalGraderId);

            if (stat) {
                stat.gradedCount++;
                stat.totalGradePoints += getSubmissionScore(submission, assignment.rubric);
                stat.totalMaxPoints += totalMaxPossible;

                criteria.forEach((criterion, i) => {
                    const aiGrading = submission.ai?.grading?.[criterion.id];
                    if (aiGrading) {
                        const aiOption = criterion.options.find((opt) => opt.id === aiGrading.optionId);
                        if (aiOption) {
                            const maxRate = criteriaMaxPoints[i];
                            const humanRate = getCriterionScore(submission, criterion);
                            const aiRate = Number(aiOption.rate);

                            stat.accuracySum += calculateAccuracyRatio(humanRate, aiRate, maxRate);
                            stat.accuracyCount++;
                        }
                    }
                });
            }
        }
    });

    return Array.from(statsMap.values()).map((s) => {
        const rawAvgGrade = s.gradedCount > 0 ? (s.totalGradePoints / s.totalMaxPoints) * 100 : 0;
        const rawAvgTime = s.gradedCount > 0 ? s.totalTimeSeconds / s.gradedCount : 0;
        const rawAiAccuracy = s.accuracyCount > 0 ? (s.accuracySum / s.accuracyCount) * 100 : 0;

        return {
            id: s.id,
            name: s.name,
            submissionsGraded: s.gradedCount,
            submissionsGradedPercentage: formatPercent(s.gradedCount, allSubmissions.length),
            avgGrade: formatPercent(s.totalGradePoints, s.totalMaxPoints, {
                decimals: 1,
                zeroOverride: Placeholder.NO_DATA,
            }),
            totalTime: formatDuration(s.totalTimeSeconds, DurationFormat.HOUR_MIN),
            avgTime: formatDuration(rawAvgTime, DurationFormat.MIN_SEC),
            aiAccuracy: formatPercent(s.accuracySum, s.accuracyCount, {
                decimals: 1,
                zeroOverride: Placeholder.NO_DATA,
            }),
            rawTime: s.totalTimeSeconds,
            rawAvgTime,
            rawAvgGrade,
            rawAiAccuracy,
        };
    });
};

/**
 * Calculates raw detailed data for all graded submissions.
 * @param {Array} submissions - List of submissions.
 * @param {Object} assignment - Assignment object.
 * @param {Array} accountUsers - List of users.
 * @returns {Array<{ id: string, studentName: string, criteriaDetails: Array, totalMaxPoints: number, totalHumanRate: number }>} Detailed data.
 */
export const calculateDetailedGradingData = (submissions, assignment, accountUsers) => {
    if (!assignment?.rubric?.criteria || !submissions || !accountUsers) return [];

    const criteria = assignment.rubric.criteria;
    const gradedSubmissions = submissions.filter((submission) => submission.grading);

    const accountUsersMap = new Map((accountUsers || []).map((u) => [String(u.owner_account_id), u]));
    const userIdsMap = new Map((accountUsers || []).map((u) => [String(u.user_id), u]));

    return gradedSubmissions.map((submission) => {
        const studentId = String(submission.user_id);
        const sourceUser = accountUsersMap.get(studentId) || userIdsMap.get(studentId) || submission.api?.user || {};

        const avatar = {
            id: sourceUser.id || studentId,
            first_name: sourceUser.first_name,
            last_name: sourceUser.last_name,
            display_name: sourceUser.display_name,
            preferredName: sourceUser.preferredName,
            profile_circle: sourceUser.profile_circle,
            profile_image: sourceUser.profile_image || sourceUser.avatar,
        };

        if (!avatar.first_name && !avatar.last_name && sourceUser.name) {
            const parts = sourceUser.name.includes(',')
                ? sourceUser.name.replace(',', '').split(' ')
                : sourceUser.name.split(' ');

            if (sourceUser.name.includes(',')) {
                avatar.first_name = parts[1];
                avatar.last_name = parts[0];
            } else {
                avatar.first_name = parts[1];
                avatar.last_name = parts[0] || '';
            }
        }

        const studentName = sourceUser.name || submission.api?.user?.name || submission.user_id || 'Unknown Student';
        const fileName = submission.files.map(({ name }) => name).join(', ') || Placeholder.NO_DATA;
        const sections = submission.api?.user?.sections?.map((s) => s.name).join(', ') || Placeholder.NO_DATA;

        const graderId = getLatestConfirmedId(submission.sessions);
        const grader = accountUsersMap.get(graderId);
        const graderName = grader?.name || grader?.owner_account_id || Placeholder.NO_DATA;

        const criteriaDetails = criteria.map((crit) => {
            const maxRate = Math.max(...crit.options.map((o) => Number(o.rate)));
            const humanRate = getCriterionScore(submission, crit);

            const humanOptionId = submission.grading?.criteria?.[crit.id];
            const humanOption = crit.options.find((o) => o.id === humanOptionId);
            const humanRating = humanOption?.title || Placeholder.NO_DATA;

            let aiRate = 0;
            let aiRating = Placeholder.NO_DATA;
            let aiAttempted = false;

            if (submission.ai?.grading?.[crit.id]) {
                aiAttempted = true;
                const aiOptionId = submission.ai.grading[crit.id].optionId;
                const aiOption = crit.options.find((o) => o.id === aiOptionId);
                if (aiOption) {
                    aiRate = Number(aiOption.rate);
                    aiRating = aiOption.title;
                }
            }

            const accuracy = aiAttempted ? calculateAccuracyRatio(humanRate, aiRate, maxRate) : null;

            return {
                id: crit.id,
                title: crit.title,
                humanRate,
                humanRating,
                aiRate,
                aiRating,
                aiAttempted,
                accuracy,
                maxRate,
            };
        });

        const totalMaxPoints = criteriaDetails.reduce((acc, c) => acc + c.maxRate, 0);
        const totalAiRate = criteriaDetails.reduce((acc, c) => acc + c.aiRate, 0);
        const totalAiAttempted = criteriaDetails.some((c) => c.aiAttempted);

        let totalHumanRate = criteriaDetails.reduce((acc, c) => acc + c.humanRate, 0);
        if (submission.grade_overrides?.total !== undefined) {
            totalHumanRate = Number(submission.grade_overrides.total);
        }

        const totalAccuracy = totalAiAttempted
            ? calculateAccuracyRatio(totalHumanRate, totalAiRate, totalMaxPoints)
            : 0;

        return {
            id: submission.id,
            avatar,
            studentName,
            fileName,
            sections,
            graderId,
            graderName,
            criteriaDetails,
            totalMaxPoints,
            totalHumanRate,
            totalAiRate,
            totalAiAttempted,
            totalAccuracy,
        };
    });
};

/**
 * Prepares the student-level statistics table data.
 * @param {Array} detailedData - Output from calculateDetailedGradingData.
 * @param {Object} filters - { criterion: string, grader: string }.
 * @param {Object} [assignment] - Current assignment object to check for API assignment type.
 * @returns {Array<{ id: string, name: string, manualRate: number, aiRate: number, accuracy: number }>} Summarized stats.
 */
export const calculateStudentLevelStats = (detailedData, filters, assignment) => {
    if (!detailedData) return [];

    const activeCritId = filters.criterion;
    const activeGraderId = filters.grader;

    let filtered = detailedData;
    if (activeGraderId !== FilterOption.ALL) {
        if (Array.isArray(activeGraderId)) {
            filtered = filtered.filter((data) => activeGraderId.includes(data.graderId));
        } else {
            filtered = filtered.filter((data) => data.graderId === activeGraderId);
        }
    }

    return filtered.map((data) => {
        let manualRate, aiRate, maxPoints, aiAttempted, accuracy;

        if (activeCritId === FilterOption.ALL) {
            manualRate = data.totalHumanRate;
            aiRate = data.totalAiRate;
            maxPoints = data.totalMaxPoints;
            aiAttempted = data.totalAiAttempted;
            accuracy = data.totalAccuracy;
        } else {
            const criterion = data.criteriaDetails.find((crit) => crit.id === activeCritId);
            manualRate = criterion?.humanRate || 0;
            aiRate = criterion?.aiRate || 0;
            maxPoints = criterion?.maxRate || 0;
            aiAttempted = criterion?.aiAttempted || false;
            accuracy = criterion?.accuracy || 0;
        }

        return {
            id: data.id,
            name: assignment?.api_assignment_id ? data.studentName : data.fileName,
            lastName: data.avatar?.last_name || '',
            avatar: data.avatar,
            manualRate,
            aiRate,
            accuracy,
            aiAttempted,
            maxPoints,
            manualGradeText: `${manualRate}/${maxPoints}`,
            aiGradeText: aiAttempted ? `${aiRate}/${maxPoints}` : Placeholder.NO_DATA,
            accuracyText: aiAttempted ? `${formatPercent(accuracy, 1, { decimals: 1 })}` : Placeholder.NO_DATA,
        };
    });
};
